import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import AlertIcon from "../../assets/icons/alert_icon.svg?react";
import TooltipTag from "../../components/UI/TooltipTag";
import { CSSProperties, ReactNode } from "react";

const colors = {
  black: "#1C1B1F",
  white: "#FFFFFF",
  green: "#283833",
  blue: "#064C68",
  red: "#73134A",
  orange: "#DF7934",
  lightGreen: "#CAE6D6",
  lightBlue: "#D2EFF9",
  lightRed: "#EBD1E0",
  lightOrange: "#FAEEDF",
  yellow: "#FEF7DF",
  darkYellow: "#3F3518",
  brown: "#A04000",
  pink: "#F2C9AE",
  lightPink: "#F8CACA",
  darkPink: "#980000",
  paleOrange: "#BE4E01",
  gray: "#D5DADC",
};

//VA Timepoint mapping
export const iconTypeMap: Record<string, ReactNode> = {
  "All Activities Completed": (
    <CheckOutlined
      data-testid="visits-activities-completion-icon-all"
      style={{ color: colors.black }}
    />
  ),
  "No Activities Completed": (
    <CloseOutlined
      data-testid="visits-activities-completion-icon-none"
      style={{ color: colors.red }}
    />
  ),
  "Some Activities Completed": (
    <AlertIcon
      data-testid="visits-activities-completion-icon-some"
      height="22"
      style={{ color: colors.orange, marginLeft: "43px" }}
    />
  ),
  "Not Required": "-",
  "Data Pending": (
    <TooltipTag
      tooltipTitle="Data not yet available."
      tagColor={colors.yellow}
      textColor={colors.darkYellow}
    >
      Data Pending
    </TooltipTag>
  ),
  "Data Missing": (
    <TooltipTag
      tooltipTitle="Expected data not found."
      tagColor={colors.pink}
      textColor={colors.brown}
    >
      Data Missing
    </TooltipTag>
  ),
  "Data Discrepancy": (
    <TooltipTag
      tooltipTitle="Data mismatch."
      tagColor={colors.lightPink}
      textColor={colors.darkPink}
    >
      Data Discrepancy
    </TooltipTag>
  ),
};

export const cellStyleMap: Record<string, CSSProperties> = {
  "All Activities Completed": {
    backgroundColor: colors.lightBlue,
    color: colors.black,
  },
  "No Activities Completed": {
    backgroundColor: colors.lightRed,
    color: colors.red,
  },
  "Some Activities Completed": {
    backgroundColor: colors.lightOrange,
    color: colors.orange,
  },
  "Data Pending": { backgroundColor: colors.white, color: colors.red },
  "Data Missing": { backgroundColor: colors.white, color: colors.red },
  "Data Discrepancy": { backgroundColor: colors.white, color: colors.red },
  "Not Required": { backgroundColor: colors.white, color: colors.gray },
  "-": { backgroundColor: colors.white, color: colors.gray }, //Value "Not Required" or "-" or ""
  1: { backgroundColor: colors.lightBlue, color: colors.black }, //Value X/X
  0: { backgroundColor: colors.lightRed, color: colors.red }, //Value 0/
  "?": { backgroundColor: colors.yellow, color: colors.red }, //Value ?/X
  "-/": { backgroundColor: colors.white, color: colors.black }, //Value -/X
  fraction: { backgroundColor: colors.lightOrange, color: colors.paleOrange }, //Value Y/X
  default: { backgroundColor: colors.lightBlue, color: colors.black }, //Value X
};

//VA Status mapping
export const textColor: Record<string, string> = {
  Active: colors.green,
  "Follow-up": colors.green, //acadia
  Randomized: colors.green, //acadia
  "Enrolled - Sprinkle Capsules": colors.green,
  "Enrolled – Sprinkle Capsules": colors.green,
  "Enrolled - Mini Capsules": colors.green,
  Complete: colors.blue,
  "End of Treatment": colors.blue,
  "Early Termination": colors.red,
  "Early Terminated": colors.red,
  Screened: colors.black,
  "Screen Failed": colors.black,
  "Screen Fail": colors.black, // acadia
  Screening: colors.black, //acadia
  "Not Enrolled": colors.black,
  "Data Pending": colors.darkYellow,
  "Data Missing": colors.brown,
  "Data Discrepancy": colors.darkPink,
};

export const borderColor: Record<string, string> = {
  Active: colors.lightGreen,
  "Follow-up": colors.lightGreen, //acadia
  Randomized: colors.lightGreen, //acadia
  "Enrolled - Sprinkle Capsules": colors.lightGreen,
  "Enrolled – Sprinkle Capsules": colors.lightGreen, //U+2013
  "Enrolled - Mini Capsules": colors.lightGreen,
  Complete: colors.lightBlue,
  "End of Treatment": colors.lightBlue,
  "Early Termination": colors.lightRed,
  "Early Terminated": colors.lightRed,
  Screened: colors.gray,
  "Screen Failed": colors.gray,
  "Screen Fail": colors.gray, // acadia
  Screening: colors.gray, //acadia
  "Not Enrolled": colors.gray,
  "Data Pending": colors.yellow,
  "Data Missing": colors.pink,
  "Data Discrepancy": colors.lightPink,
};

export const pillColor: Record<string, string> = {
  Active: colors.lightGreen,
  "Follow-up": colors.lightGreen, //acadia
  Randomized: colors.lightGreen, //acadia
  "Enrolled - Sprinkle Capsules": colors.lightGreen,
  "Enrolled – Sprinkle Capsules": colors.lightGreen,
  "Enrolled - Mini Capsules": colors.lightGreen,
  Complete: colors.lightBlue,
  "End of Treatment": colors.lightBlue,
  "Early Termination": colors.lightRed,
  "Early Terminated": colors.lightRed,
  Screened: colors.gray,
  "Screen Failed": colors.gray,
  "Screen Fail": colors.gray, // acadia
  Screening: colors.gray, //acadia
  "Not Enrolled": colors.gray,
  "Data Pending": colors.yellow,
  "Data Missing": colors.pink,
  "Data Discrepancy": colors.lightPink,
};

export const tooltipTitle: Record<string, string> = {
  Active: "Subject is part of a study that is ongoing.",
  "Follow-up": "Subject undergoing safety follow-up.", //acadia
  Randomized:
    "Subject has been randomized and is part of a study that is ongoing.", //acadia
  "Enrolled - Sprinkle Capsules":
    "Subject is enrolled and receiving Sprinkle Capsules.",
  "Enrolled - Mini Capsules":
    "Subject is enrolled and receiving Mini Capsules.",
  Complete: "Subject completed the study.",
  "End of Treatment": "Subject completed the treatment",
  "Early Termination": "Subject discontinued study prior to completion.",
  "Early Terminated": "Subject discontinued study prior to completion.",
  Screened: "Subject has been screened for eligibility.",
  "Screen Failed": "Subject failed the screening process.",
  "Screen Fail": "Subject failed the screening process.", // acadia
  Screening: "Subject undergoing screening for eligibility.", //acadia
  "Not Enrolled":
    "Subject has not been randomized and is not enrolled in the study.",
  "Data Pending": "Data not yet available.",
  "Data Missing": "Expected data not found.",
  "Data Discrepancy": "Data mismatch.",
};

export const dataStatus: Record<string, string> = {
  "Data Pending": "Data Pending",
  "Data Missing": "Data Missing",
  "Data Discrepancy": "Data Discrepancy",
};
